import React, { useEffect, useMemo, useState } from 'react';
import {
    AppBar,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import DomainRoundedIcon from '@mui/icons-material/DomainRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { LoginMenu } from './api-authorization/LoginMenu';
import authService from './api-authorization/AuthorizeService';
import AppRoutes from '../AppRoutes';

const routeIcons = {
    '/refunds': ReceiptLongRoundedIcon,
    '/reports': AssessmentRoundedIcon,
    '/management': DomainRoundedIcon,
    '/refunds/upload': CloudUploadRoundedIcon,
    '/cards': CreditCardRoundedIcon,
    '/user-management': ManageAccountsRoundedIcon,
    '/email-settings': EmailRoundedIcon,
    '/api-keys': KeyRoundedIcon
};

const isRouteActive = (pathname, path) => {
    if (pathname === '/refunds/upload' && path === '/refunds') return false;
    return pathname === path || (path !== '/' && pathname.startsWith(`${path}/`));
};

export const NavMenu = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [systemMenuAnchor, setSystemMenuAnchor] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();

    useEffect(() => {
        const subscription = authService.subscribe(() => populateState());
        populateState();

        return () => authService.unsubscribe(subscription);
    }, []);

    useEffect(() => {
        setDrawerOpen(false);
        setSystemMenuAnchor(null);
    }, [location.pathname]);

    const populateState = async () => {
        const [authenticated, currentUser] = await Promise.all([
            authService.isAuthenticated(),
            authService.getUser()
        ]);
        setIsAuthenticated(authenticated);
        setUser(currentUser);
    };

    const menuItems = useMemo(() => {
        const profileRoles = user?.profile?.role;
        const userRoles = Array.isArray(profileRoles)
            ? profileRoles
            : profileRoles
                ? [profileRoles]
                : [];

        return AppRoutes.filter(route => {
            if (!route.name || !route.path) return false;
            if (!route.requireAuth) return true;
            if (!isAuthenticated) return false;
            if (!route.roles) return true;
            return route.roles.some(role => userRoles.includes(role));
        });
    }, [isAuthenticated, user]);

    const primaryItems = menuItems.filter(route => route.navGroup === 'primary');
    const systemItems = menuItems.filter(route => route.navGroup === 'system');
    const systemIsActive = systemItems.some(route => isRouteActive(location.pathname, route.path));

    const navButtonSx = active => ({
        px: 1.5,
        py: 1,
        borderRadius: 2,
        color: active ? 'primary.main' : 'text.secondary',
        bgcolor: active ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
        fontWeight: active ? 700 : 600,
        textTransform: 'none',
        '&:hover': {
            bgcolor: active ? 'rgba(25, 118, 210, 0.12)' : 'action.hover',
            color: 'text.primary'
        }
    });

    const renderMenuItem = route => {
        const Icon = routeIcons[route.path] || SettingsRoundedIcon;
        const active = isRouteActive(location.pathname, route.path);

        return (
            <MenuItem
                component={Link}
                to={route.path}
                key={route.path}
                selected={active}
                onClick={() => setSystemMenuAnchor(null)}
                sx={{ borderRadius: 1.5, mx: 0.75, my: 0.25, minHeight: 44 }}
            >
                <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                <ListItemText primary={route.name} />
            </MenuItem>
        );
    };

    return (
        <AppBar
            position="sticky"
            color="transparent"
            elevation={0}
            sx={{
                mb: 3,
                bgcolor: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(14px)',
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}
        >
            <Toolbar sx={{ width: '100%', maxWidth: 1536, mx: 'auto', minHeight: { xs: 60, md: 68 }, gap: 2 }}>
                <Stack
                    component={Link}
                    to="/"
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    sx={{ color: 'text.primary', textDecoration: 'none', mr: { md: 2 } }}
                >
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2.25,
                        display: 'grid',
                        placeItems: 'center',
                        color: 'primary.contrastText',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        boxShadow: '0 8px 20px rgba(25, 118, 210, 0.22)'
                    }}>
                        <AccountBalanceWalletRoundedIcon fontSize="small" />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.02em' }}>
                            SmartCashBack
                        </Typography>
                    </Box>
                </Stack>

                <Box sx={{ flexGrow: 1 }} />

                {!isMobile && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        {primaryItems.map(route => {
                            const Icon = routeIcons[route.path] || ReceiptLongRoundedIcon;
                            return (
                                <Button
                                    component={Link}
                                    to={route.path}
                                    key={route.path}
                                    startIcon={<Icon fontSize="small" />}
                                    sx={navButtonSx(isRouteActive(location.pathname, route.path))}
                                >
                                    {route.name}
                                </Button>
                            );
                        })}

                        {systemItems.length > 0 && (
                            <>
                                <Button
                                    onClick={event => setSystemMenuAnchor(event.currentTarget)}
                                    startIcon={<SettingsRoundedIcon fontSize="small" />}
                                    endIcon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
                                    sx={{
                                        ...navButtonSx(systemIsActive),
                                        color: systemIsActive ? 'primary.main' : 'text.secondary',
                                        bgcolor: systemIsActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
                                    }}
                                >
                                    Система
                                </Button>
                                <Menu
                                    anchorEl={systemMenuAnchor}
                                    open={Boolean(systemMenuAnchor)}
                                    onClose={() => setSystemMenuAnchor(null)}
                                    PaperProps={{ sx: { mt: 1, minWidth: 245, borderRadius: 2.5, p: 0.5 } }}
                                >
                                    {systemItems.map(renderMenuItem)}
                                </Menu>
                            </>
                        )}

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <LoginMenu />
                    </Stack>
                )}

                {isMobile && (
                    <IconButton
                        color="inherit"
                        aria-label="Открыть меню"
                        onClick={() => setDrawerOpen(true)}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                    >
                        <MenuRoundedIcon />
                    </IconButton>
                )}
            </Toolbar>

            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: { xs: 300, sm: 340 }, p: 2 }} role="navigation">
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="h6" fontWeight={800}>Меню</Typography>
                        <IconButton aria-label="Закрыть меню" onClick={() => setDrawerOpen(false)}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </Stack>

                    {primaryItems.length > 0 && (
                        <>
                            <Typography variant="overline" color="text.secondary" sx={{ px: 2 }}>
                                Работа
                            </Typography>
                            <List sx={{ pt: 0.5 }}>
                                {primaryItems.map(route => {
                                    const Icon = routeIcons[route.path] || ReceiptLongRoundedIcon;
                                    return (
                                        <ListItemButton
                                            component={Link}
                                            to={route.path}
                                            key={route.path}
                                            selected={isRouteActive(location.pathname, route.path)}
                                            sx={{ borderRadius: 2, mb: 0.5 }}
                                        >
                                            <ListItemIcon><Icon /></ListItemIcon>
                                            <ListItemText primary={route.name} />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </>
                    )}

                    {systemItems.length > 0 && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="overline" color="text.secondary" sx={{ px: 2 }}>
                                Система
                            </Typography>
                            <List sx={{ pt: 0.5 }}>
                                {systemItems.map(route => {
                                    const Icon = routeIcons[route.path] || SettingsRoundedIcon;
                                    return (
                                        <ListItemButton
                                            component={Link}
                                            to={route.path}
                                            key={route.path}
                                            selected={isRouteActive(location.pathname, route.path)}
                                            sx={{ borderRadius: 2, mb: 0.5 }}
                                        >
                                            <ListItemIcon><Icon /></ListItemIcon>
                                            <ListItemText primary={route.name} />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </>
                    )}

                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ px: 1 }}><LoginMenu /></Box>
                </Box>
            </Drawer>
        </AppBar>
    );
};
